import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form } from '@/components/ui/form';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';

// Import extracted modules
import {
  getFinancialIntermediaryTypes,
  getLegalForms,
} from './identification/constants';
import { useConditionalFields } from './identification/hooks/useConditionalFields';
import { useFormEffects } from './identification/hooks/useFormEffects';
import { createIdentificationSchema } from './identification/schema';
import {
  IdentificationFormData,
  IdentificationFormProps,
} from './identification/types';
import {
  getDefaultExecutiveValues,
  getDefaultPepValues,
} from './identification/utils';

// Import section components
import { AdditionalDetailsSection } from './identification/components/AdditionalDetailsSection';
import { BusinessActivitiesSection } from './identification/components/BusinessActivitiesSection';
import { ContractingPartySection } from './identification/components/ContractingPartySection';
import { ExecutivesSection } from './identification/components/ExecutivesSection';
import { OperationalOfficeSection } from './identification/components/OperationalOfficeSection';
import { PepSection } from './identification/components/PepSection';

/**
 * IdentificationForm Component
 *
 * A comprehensive form for collecting KYC identification information including:
 * - Company details and legal structure
 * - Financial intermediary status
 * - Listed company information
 * - Business activities and operational office
 * - Executive bodies and authorized representatives
 * - Politically Exposed Persons (PEP) declarations
 *
 * Features:
 * - Conditional field visibility based on user selections
 * - Multi-language support via react-i18next
 * - Real-time validation with Zod schemas
 * - Dynamic field arrays for executives and PEPs
 * - Responsive grid layouts for mobile and desktop
 *
 * @param {IdentificationFormProps} props - Component props
 * @param {React.Ref} ref - Forwarded ref exposing triggerValidation method
 * @returns {JSX.Element} Rendered identification form
 */
export const IdentificationForm = forwardRef<
  { triggerValidation: () => Promise<boolean> },
  IdentificationFormProps
>(function IdentificationForm(
  { onSubmit, onError, onChange, className, defaultValues },
  ref
) {
  const { t: tForms, language } = useFormsTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Create schema with translations - memoized to avoid recreation on every render
  const identificationSchema = useMemo(
    () => createIdentificationSchema(tForms),
    [tForms]
  );

  // Memoized default values
  const defaultExecutiveValues = useMemo(() => getDefaultExecutiveValues(), []);
  const defaultPepValues = useMemo(() => getDefaultPepValues(), []);

  // Initialize form
  const form = useForm<IdentificationFormData>({
    resolver: zodResolver(identificationSchema),
    mode: 'all', // Validate on all events (blur, change, submit, trigger)
    defaultValues: {
      companyName: '',
      headOfficeAddress: '',
      postcode: '',
      city: '',
      country: '',
      legalForm: '',
      legalFormOther: '',
      isFinancialIntermediary: '',
      financialIntermediaryType: '',
      financialIntermediaryTypeOther: '',
      supervisoryOrganization: '',
      listedCompany: '',
      stockExchange: '',
      tickerSymbol: '',
      subsidiaryOwnership: '',
      parentCompanyExchange: '',
      parentTicker: '',
      employees: '',
      shellCompany: '',
      businessDescription: '',
      sameAddressAsContractingParty: false,
      operationalOfficeCompanyName: '',
      operationalOfficeAddress: '',
      operationalOfficePostcode: '',
      operationalOfficeCity: '',
      operationalOfficeCountry: '',
      isPepInvolved: '',
      politicallyExposedPersons: [],
      executives: [defaultExecutiveValues],
      powerOfAttorneyAttached: undefined,
      ...defaultValues,
    },
  });

  // Field arrays for executives and PEPs
  const executiveFieldArray = useFieldArray({
    control: form.control,
    name: 'executives',
  });

  const pepFieldArray = useFieldArray({
    control: form.control,
    name: 'politicallyExposedPersons',
  });

  // Watch fields for conditional rendering - OPTIMIZED: Single batch watcher
  // Consolidates 7+ individual useWatch calls into one to reduce re-renders
  const conditionalFieldValues = useWatch({
    control: form.control,
    name: [
      'country',
      'isFinancialIntermediary',
      'financialIntermediaryType',
      'listedCompany',
      'subsidiaryOwnership',
      'legalForm',
      'isPepInvolved',
      'sameAddressAsContractingParty',
    ],
  });

  // Destructure watched values for easier access
  const [
    country,
    isFinancialIntermediary,
    financialIntermediaryType,
    listedCompany,
    subsidiaryOwnership,
    legalForm,
    isPepInvolved,
    sameAddressAsContractingParty,
  ] = conditionalFieldValues;

  // Memoize watched values object to prevent unnecessary recalculations
  const watchedValues = useMemo(
    () => ({
      country,
      isFinancialIntermediary,
      financialIntermediaryType,
      listedCompany,
      subsidiaryOwnership,
      legalForm,
      isPepInvolved: isPepInvolved as string | undefined,
      sameAddressAsContractingParty,
    }),
    [
      country,
      isFinancialIntermediary,
      financialIntermediaryType,
      listedCompany,
      subsidiaryOwnership,
      legalForm,
      isPepInvolved,
      sameAddressAsContractingParty,
    ]
  );

  // Calculate conditional field visibility - now with memoized input
  const conditionalVisibility = useConditionalFields(watchedValues);

  // Setup form side effects (field clearing, copying, etc.)
  useFormEffects({
    form,
    conditionalVisibility,
    sameAddressAsContractingParty: watchedValues.sameAddressAsContractingParty,
    pepFieldArray,
    defaultPepValues,
    onChange,
  });

  // Re-trigger validation when language changes to update error messages
  useEffect(() => {
    const { errors } = form.formState;
    if (Object.keys(errors).length > 0) {
      form.trigger();
    }
  }, [form, language]);

  // Expose form validation method to parent
  useImperativeHandle(ref, () => ({
    triggerValidation: async () => {
      const isValid = await form.trigger();
      return isValid;
    },
  }));

  // Memoize configuration options
  const legalForms = useMemo(() => getLegalForms(tForms), [tForms]);
  const financialIntermediaryTypes = useMemo(
    () => getFinancialIntermediaryTypes(tForms),
    [tForms]
  );

  // Submit handler
  const handleSubmit = useCallback(
    async (data: IdentificationFormData) => {
      setSubmitError(null);

      try {
        onSubmit?.(data);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'An error occurred';
        setSubmitError(errorMessage);
        onError?.(error as Error);
      }
    },
    [onSubmit, onError]
  );

  return (
    <div className={className}>
      {submitError && (
        <Alert variant='destructive' className='mb-6'>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form
          id='identification-form'
          onSubmit={form.handleSubmit(handleSubmit)}
          className='space-y-6'
        >
          {/* Form Header */}
          <div className='text-center mb-6'>
            <h1 className='text-2xl font-bold mb-2'>
              {tForms('identification.title')}
            </h1>
          </div>

          {/* 1. Contracting Party Details */}
          <ContractingPartySection
            control={form.control}
            country={country || ''}
            legalForm={legalForm || ''}
            legalForms={legalForms}
          />

          {/* 1.1 Additional contracting party details */}
          <AdditionalDetailsSection
            control={form.control}
            showFinancialIntermediaryFields={
              conditionalVisibility.showFinancialIntermediaryFields
            }
            showCorporateFields={conditionalVisibility.showCorporateFields}
            showListedCompanyField={
              conditionalVisibility.showListedCompanyField
            }
            showListedCompanyFields={
              conditionalVisibility.showListedCompanyFields
            }
            showParentSubsidiaryFields={
              conditionalVisibility.showParentSubsidiaryFields
            }
            financialIntermediaryTypes={financialIntermediaryTypes}
            financialIntermediaryType={watchedValues.financialIntermediaryType}
          />

          {/* 1.2 Information on business activities */}
          <BusinessActivitiesSection control={form.control} />

          {/* 1.3 Operational Office */}
          <OperationalOfficeSection
            control={form.control}
            showOperationalOfficeFields={
              conditionalVisibility.showOperationalOfficeFields
            }
          />

          {/* 2. Executive Bodies / Authorized representatives */}
          <ExecutivesSection
            control={form.control}
            fields={executiveFieldArray.fields}
            append={executiveFieldArray.append}
            remove={executiveFieldArray.remove}
            defaultExecutiveValues={defaultExecutiveValues}
          />

          {/* 3 Politically Exposed Persons (PEP) */}
          <PepSection
            control={form.control}
            showPepFields={conditionalVisibility.showPepFields}
            pepFields={pepFieldArray.fields}
            appendPep={pepFieldArray.append}
            removePep={pepFieldArray.remove}
            defaultPepValues={defaultPepValues}
          />
        </form>
      </Form>
    </div>
  );
});

export default IdentificationForm;
